/**
 * Export des données d'exportation (surfaces traitées) en PDF professionnel
 * Avec en-tête, titre, logo, tableau et pied de page
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const APP_NAME = "Road Alert AGEROUTE";
const MARGIN = 14;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const FOOTER_HEIGHT = 12;

export interface ExportationDataRowPdf {
  // Onglet 1 (surfaces-itineraire)
  itineraire?: string;
  dateitineraire?: string;
  surfacetraitee?: number;
  nbrepad?: number;
  nbrepcd?: number;
  communes?: string;
  libelledepartement?: string;
  // Onglet 2 (surfaces-departement)
  departement?: string;
  iddepartement?: number;
  annee?: string;
  // Onglet 3 (cumul-itineraire)
  idCumulItineraire?: number;
  mois?: string;
  surfacePrevisionnel?: number;
  surfaceTraite?: number;
  resteSurface?: number;
  statut?: number;
}

function formatDateFr(dateStr: string): string {
  if (!dateStr) return "-";
  try {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

export interface ExportExportationDataPdfOptions {
  data: ExportationDataRowPdf[];
  dateDebut: string;
  dateFin: string;
  filename?: string;
  title?: string;
  /** Data URL du logo pour l'en-tête */
  logoDataUrl?: string | null;
}

const LOGO_HEADER_HEIGHT_MM = 12;
const LOGO_MAX_WIDTH_MM = 45;

export function exportExportationDataPdf({
  data,
  dateDebut,
  dateFin,
  filename = "rapport-exportation-data.pdf",
  title = "Rapport des surfaces traitées par Itinéraire",
  logoDataUrl,
}: ExportExportationDataPdfOptions): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const contentWidth = PAGE_WIDTH - 2 * MARGIN;
  let currentY = MARGIN;

  // --- En-tête avec logo ---
  if (logoDataUrl) {
    try {
      const imgW = LOGO_MAX_WIDTH_MM;
      const imgH = LOGO_HEADER_HEIGHT_MM;
      doc.addImage(logoDataUrl, "PNG", MARGIN, currentY - 2, imgW, imgH);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(APP_NAME, MARGIN + imgW + 4, currentY + 5);
    } catch {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(APP_NAME, MARGIN, currentY + 4);
    }
  } else {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(APP_NAME, MARGIN, currentY + 4);
  }
  currentY += LOGO_HEADER_HEIGHT_MM + 2;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, currentY, PAGE_WIDTH - MARGIN, currentY);
  currentY += 10;

  // --- Titre ---
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(title, MARGIN, currentY);
  currentY += 8;

  // --- Période ---
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Période : du ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`, MARGIN, currentY);
  currentY += 6;

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, MARGIN, currentY);
  doc.setTextColor(0, 0, 0);
  currentY += 12;

  // --- Tableau ---
  // Détection automatique du type de données (onglet 1, 2 ou 3)
  const isDepartementTab = data.length > 0 && data[0]?.departement != null && data[0]?.iddepartement != null;
  const isCumulTab = data.length > 0 && data[0]?.surfacePrevisionnel != null;
  
  const tableColumns = isCumulTab
    ? ["Itinéraire", "Année", "Mois", "Surface prévisionnelle (m²)", "Surface traitée (m²)", "Reste surface (m²)"]
    : isDepartementTab
    ? ["Département", "Année", "Surface traitée (m²)", "PAD", "PCD"]
    : ["Itinéraire", "Date", "Surface traitée (m²)", "PAD", "PCD", "Commune"];
  
  const moisNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];
  
  const tableRows = data.map((row) => {
    if (isCumulTab) {
      const moisNum = row.mois ? parseInt(String(row.mois), 10) : null;
      const moisLabel = moisNum && moisNum >= 1 && moisNum <= 12 ? moisNames[moisNum - 1] : (row.mois ?? "-");
      return [
        row.itineraire ?? "-",
        row.annee ?? "-",
        moisLabel,
        row.surfacePrevisionnel != null ? String(row.surfacePrevisionnel.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
        row.surfaceTraite != null ? String(row.surfaceTraite.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
        row.resteSurface != null ? String(row.resteSurface.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
      ];
    } else if (isDepartementTab) {
      return [
        row.departement ?? "-",
        row.annee ?? "-",
        row.surfacetraitee != null ? String(row.surfacetraitee.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
        row.nbrepad != null ? String(row.nbrepad.toFixed(0)) : "-",
        row.nbrepcd != null ? String(row.nbrepcd.toFixed(0)) : "-",
      ];
    } else {
      return [
        row.itineraire ?? "-",
        formatDateFr(row.dateitineraire ?? ""),
        row.surfacetraitee != null ? String(row.surfacetraitee.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })) : "-",
        row.nbrepad != null ? String(row.nbrepad.toFixed(0)) : "-",
        row.nbrepcd != null ? String(row.nbrepcd.toFixed(0)) : "-",
        row.communes ?? "-",
      ];
    }
  });

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: currentY,
    margin: { left: MARGIN, right: MARGIN },
    tableWidth: contentWidth,
    theme: "grid",
    headStyles: {
      fillColor: [66, 139, 202],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    didDrawPage: (data) => {
      const pageCount = doc.getNumberOfPages();
      const pageNumber = data.pageNumber;
      const footerY = PAGE_HEIGHT - FOOTER_HEIGHT;

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, footerY - 3, PAGE_WIDTH - MARGIN, footerY - 3);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`${APP_NAME} - ${title} - Page ${pageNumber}/${pageCount}`, MARGIN, footerY);
      doc.text(`Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))}`, PAGE_WIDTH - MARGIN - 50, footerY, { align: "right" });
      doc.setTextColor(0, 0, 0);
    },
  });

  doc.save(filename);
}
